import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiDelete, apiGet, apiPatch, apiPost } from '../lib/api';
import { formatCents, splitServiceName } from '../lib/format';
import { SkeletonList } from '../components/Skeleton';
import { toast } from '../lib/toast';

interface Service {
  id: number;
  name: string;
  duration_min: number;
  buffer_min: number;
  price_cents: number;
  color: string | null;
  active: number;
  sort_order: number;
}

export function SettingsServices() {
  const [items, setItems] = useState<Service[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState<{ name: string; duration_min: string; price_cents: string }>({ name: '', duration_min: '', price_cents: '' });
  const [creating, setCreating] = useState(false);
  const [createDraft, setCreateDraft] = useState({ name: '', duration_min: '60', price_cents: '5000' });

  async function load() {
    try {
      const r = await apiGet<{ services: Service[] }>('/api/services?include_inactive=1');
      setItems(r.services);
    } catch (e) {
      setErr((e as Error).message);
    }
  }
  useEffect(() => { load(); }, []);

  function startEdit(s: Service) {
    setEditing(s.id);
    setDraft({
      name: s.name,
      duration_min: String(s.duration_min),
      price_cents: String(s.price_cents),
    });
  }

  async function save(id: number) {
    try {
      await apiPatch(`/api/services/${id}`, {
        name: draft.name.trim(),
        duration_min: parseInt(draft.duration_min, 10),
        price_cents: parseInt(draft.price_cents, 10),
      });
      setEditing(null);
      toast.success('Service saved.');
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function toggleActive(s: Service) {
    try {
      await apiPatch(`/api/services/${s.id}`, { active: s.active ? 0 : 1 });
      toast.success(s.active ? 'Hidden.' : 'Restored.');
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function remove(s: Service) {
    if (!confirm(`Remove ${s.name}? Past bookings stay; the service is hidden from new bookings.`)) return;
    try {
      await apiDelete(`/api/services/${s.id}`);
      toast.success(`${s.name} removed.`);
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function createNew() {
    try {
      await apiPost('/api/services', {
        name: createDraft.name.trim(),
        duration_min: parseInt(createDraft.duration_min, 10),
        price_cents: parseInt(createDraft.price_cents, 10),
      });
      setCreating(false);
      setCreateDraft({ name: '', duration_min: '60', price_cents: '5000' });
      toast.success('Service created.');
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  if (err) return <div className="text-rose-400">{err}</div>;

  // Group by base name for display
  const groups: Record<string, Service[]> = {};
  for (const s of items || []) {
    const { base } = splitServiceName(s.name);
    (groups[base] ||= []).push(s);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Services</h1>
        <Link to="/settings" className="text-sm text-muted">← Settings</Link>
      </div>

      {!creating ? (
        <button onClick={() => setCreating(true)} className="btn-primary w-full">+ Add service</button>
      ) : (
        <div className="card space-y-3">
          <div><label className="label">Name</label><input className="input" placeholder="e.g. Nail trim" value={createDraft.name} onChange={(e) => setCreateDraft({ ...createDraft, name: e.target.value })} /></div>
          <div><label className="label">Duration (min)</label><input className="input" type="number" value={createDraft.duration_min} onChange={(e) => setCreateDraft({ ...createDraft, duration_min: e.target.value })} /></div>
          <div><label className="label">Price (cents)</label><input className="input" type="number" value={createDraft.price_cents} onChange={(e) => setCreateDraft({ ...createDraft, price_cents: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setCreating(false)} className="btn-ghost">Cancel</button>
            <button onClick={createNew} className="btn-primary">Create</button>
          </div>
        </div>
      )}

      {!items ? (
        <SkeletonList count={4} variant="row" />
      ) : (
        Object.entries(groups).map(([base, list]) => (
          <div key={base} className="space-y-2">
            <h3 className="font-semibold text-text/90 mt-3">{base}</h3>
            {list.map((s) => (
              <div key={s.id} className={`card ${!s.active ? 'opacity-50' : ''}`}>
                {editing === s.id ? (
                  <div className="space-y-2">
                    <input className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className="label">Min</label><input className="input" type="number" value={draft.duration_min} onChange={(e) => setDraft({ ...draft, duration_min: e.target.value })} /></div>
                      <div><label className="label">Price (¢)</label><input className="input" type="number" value={draft.price_cents} onChange={(e) => setDraft({ ...draft, price_cents: e.target.value })} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => setEditing(null)} className="btn-ghost text-sm py-2">Cancel</button>
                      <button onClick={() => save(s.id)} className="btn-primary text-sm py-2">Save</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{splitServiceName(s.name).tier || s.name}</div>
                      <div className="text-sm text-muted"><span className="font-mono">{s.duration_min}</span> min · <span className="font-mono">{formatCents(s.price_cents)}</span>{!s.active && ' · inactive'}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => startEdit(s)} className="btn-ghost px-3 py-2 text-sm">Edit</button>
                      <button onClick={() => toggleActive(s)} className="btn-ghost px-3 py-2 text-sm">{s.active ? 'Hide' : 'Show'}</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}
